import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  BookOpen,
  Video,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Search,
  Filter,
  Shield,
  Users,
  Clock,
  Sparkles,
  Play,
  ChevronRight,
  FileText,
  Settings,
  Loader2
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'dashboard', label: 'Tableau de bord' },
  { value: 'calendar', label: 'Calendrier/Agenda' },
  { value: 'projects', label: 'Projets' },
  { value: 'chat', label: 'Chat interne' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'clients', label: 'Clients' },
  { value: 'team', label: 'Équipe' },
  { value: 'billing', label: 'Facturation' },
  { value: 'settings', label: 'Paramètres' },
  { value: 'tools', label: 'Outils' },
  { value: 'other', label: 'Autres' }
];

const ACCOUNT_TYPES = [
  { value: 'agency', label: 'Agence/Admin', color: '#6366F1' },
  { value: 'project_manager', label: 'Chef de projet', color: '#8B5CF6' },
  { value: 'customer_service', label: 'Service client', color: '#EC4899' },
  { value: 'team_member', label: 'Membre équipe', color: '#10B981' },
  { value: 'client', label: 'Client', color: '#F59E0B' },
  { value: 'affiliate', label: 'Apporteur d\'affaires', color: '#EF4444' }
];

const TechnicalDocsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('features');
  const [features, setFeatures] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAccountType, setSelectedAccountType] = useState('all');
  
  // Dialog states
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  const [showTimelineDialog, setShowTimelineDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [featureForm, setFeatureForm] = useState({
    name: '', description: '', category: '', account_types: [], version: '',
    added_date: new Date().toISOString().split('T')[0],
    tutorial_content: '',
    tutorial_steps: [],
    related_pages: []
  });
  const [tutorialForm, setTutorialForm] = useState({
    title: '', description: '', youtube_url: '', category: '', account_types: [], duration_minutes: 0
  });
  const [timelineForm, setTimelineForm] = useState({ title: '', description: '', entry_type: 'update' });
  
  // Step input for tutorial
  const [newStep, setNewStep] = useState('');
  
  // Selected feature for detail view
  const [selectedFeature, setSelectedFeature] = useState(null);

  const isAdmin = user?.role === 'agency';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [featuresRes, tutorialsRes, timelineRes] = await Promise.all([
        api.get('/docs/features'),
        api.get('/docs/tutorials'),
        api.get('/docs/timeline')
      ]);
      setFeatures(featuresRes.data || []);
      setTutorials(tutorialsRes.data || []);
      setTimeline(timelineRes.data || []);
    } catch (error) {
      console.error('Error fetching docs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtering
  const filteredFeatures = features.filter(f => {
    const matchesSearch = f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         f.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || f.category === selectedCategory;
    const matchesAccount = selectedAccountType === 'all' || f.account_types?.includes(selectedAccountType);
    return matchesSearch && matchesCategory && matchesAccount;
  });

  const filteredTutorials = tutorials.filter(t => {
    const matchesSearch = t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    const matchesAccount = selectedAccountType === 'all' || t.account_types?.includes(selectedAccountType);
    return matchesSearch && matchesCategory && matchesAccount;
  });

  // CRUD operations
  const handleSaveFeature = async () => {
    try {
      const dataToSend = {
        ...featureForm,
        tutorial_steps: featureForm.tutorial_steps || [],
        related_pages: featureForm.related_pages || []
      };
      
      if (editingItem) {
        await api.put(`/docs/features/${editingItem.id}`, dataToSend);
        toast.success('Fonctionnalité mise à jour');
      } else {
        await api.post('/docs/features', dataToSend);
        toast.success('Fonctionnalité ajoutée');
      }
      setShowFeatureDialog(false);
      setEditingItem(null);
      setFeatureForm({ 
        name: '', description: '', category: '', account_types: [], version: '',
        added_date: new Date().toISOString().split('T')[0],
        tutorial_content: '',
        tutorial_steps: [],
        related_pages: []
      });
      setNewStep('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };
  
  const addTutorialStep = () => {
    if (newStep.trim()) {
      setFeatureForm({
        ...featureForm,
        tutorial_steps: [...(featureForm.tutorial_steps || []), newStep.trim()]
      });
      setNewStep('');
    }
  };
  
  const removeTutorialStep = (index) => {
    setFeatureForm({
      ...featureForm,
      tutorial_steps: featureForm.tutorial_steps.filter((_, i) => i !== index)
    });
  };

  const handleSaveTutorial = async () => {
    try {
      if (editingItem) {
        await api.put(`/docs/tutorials/${editingItem.id}`, tutorialForm);
        toast.success('Tutoriel mis à jour');
      } else {
        await api.post('/docs/tutorials', tutorialForm);
        toast.success('Tutoriel ajouté');
      }
      setShowTutorialDialog(false);
      setEditingItem(null);
      setTutorialForm({ title: '', description: '', youtube_url: '', category: '', account_types: [], duration_minutes: 0 });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleSaveTimeline = async () => {
    try {
      await api.post('/docs/timeline', timelineForm);
      toast.success('Entrée ajoutée à la timeline');
      setShowTimelineDialog(false);
      setTimelineForm({ title: '', description: '', entry_type: 'update' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteFeature = async (id) => {
    if (!window.confirm('Supprimer cette fonctionnalité ?')) return;
    try {
      await api.delete(`/docs/features/${id}`);
      toast.success('Fonctionnalité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteTutorial = async (id) => {
    if (!window.confirm('Supprimer ce tutoriel ?')) return;
    try {
      await api.delete(`/docs/tutorials/${id}`);
      toast.success('Tutoriel supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const editFeature = (feature) => {
    setEditingItem(feature);
    setFeatureForm({
      name: feature.name,
      description: feature.description,
      category: feature.category,
      account_types: feature.account_types || [],
      version: feature.version || '',
      added_date: feature.added_date || new Date().toISOString().split('T')[0],
      tutorial_content: feature.tutorial_content || '',
      tutorial_steps: feature.tutorial_steps || [],
      related_pages: feature.related_pages || []
    });
    setShowFeatureDialog(true);
  };

  const editTutorial = (tutorial) => {
    setEditingItem(tutorial);
    setTutorialForm({
      title: tutorial.title,
      description: tutorial.description,
      youtube_url: tutorial.youtube_url,
      category: tutorial.category,
      account_types: tutorial.account_types || [],
      duration_minutes: tutorial.duration_minutes || 0
    });
    setShowTutorialDialog(true);
  };

  const getYouTubeEmbedUrl = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getAccountTypeLabel = (type) => {
    const found = ACCOUNT_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getAccountTypeColor = (type) => {
    const found = ACCOUNT_TYPES.find(t => t.value === type);
    return found ? found.color : '#64748b';
  };

  const getCategoryLabel = (category) => {
    const found = CATEGORIES.find(c => c.value === category);
    return found ? found.label : category;
  };

  const toggleAccountType = (type, form, setForm) => {
    const current = form.account_types || [];
    if (current.includes(type)) {
      setForm({ ...form, account_types: current.filter(t => t !== type) });
    } else {
      setForm({ ...form, account_types: [...current, type] });
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BookOpen className="w-7 h-7" style={{ color: theme?.colors?.accent }} />
              Documentation Technique
            </h1>
            <p className="text-slate-500 mt-1">
              Fonctionnalités, tutoriels et guide d'utilisation
            </p>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowTimelineDialog(true)}
              >
                <Clock className="w-4 h-4 mr-2" />
                Ajouter mise à jour
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedAccountType} onValueChange={setSelectedAccountType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type de compte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les comptes</SelectItem>
                  {ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="features" className="gap-2">
              <Sparkles className="w-4 h-4" />
              Fonctionnalités ({filteredFeatures.length})
            </TabsTrigger>
            <TabsTrigger value="tutorials" className="gap-2">
              <Video className="w-4 h-4" />
              Tutoriels ({filteredTutorials.length})
            </TabsTrigger>
            <TabsTrigger value="permissions" className="gap-2">
              <Shield className="w-4 h-4" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="timeline" className="gap-2">
              <Clock className="w-4 h-4" />
              Mises à jour
            </TabsTrigger>
          </TabsList>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-4">
            {isAdmin && (
              <Button onClick={() => { setEditingItem(null); setShowFeatureDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une fonctionnalité
              </Button>
            )}
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredFeatures.length === 0 ? (
              <Card className="card">
                <CardContent className="py-12 text-center text-slate-500">
                  Aucune fonctionnalité trouvée
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredFeatures.map(feature => (
                  <Card 
                    key={feature.id} 
                    className="card hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedFeature(feature)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(feature.category)}
                            </Badge>
                            {feature.version && (
                              <Badge variant="secondary" className="text-xs">
                                v{feature.version}
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-base">{feature.name}</CardTitle>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => editFeature(feature)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteFeature(feature.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{feature.description}</p>
                      
                      {/* Tutorial indicator */}
                      {(feature.tutorial_content || feature.tutorial_steps?.length > 0) && (
                        <div className="flex items-center gap-1 text-xs text-emerald-600 mb-2">
                          <FileText className="w-3 h-3" />
                          Tutoriel disponible
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-1">
                        {feature.account_types?.map(type => (
                          <Badge 
                            key={type} 
                            className="text-xs text-white"
                            style={{ backgroundColor: getAccountTypeColor(type) }}
                          >
                            {getAccountTypeLabel(type)}
                          </Badge>
                        ))}
                      </div>
                      {feature.added_date && (
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Ajouté le {new Date(feature.added_date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Feature Detail Dialog */}
            <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                {selectedFeature && (
                  <>
                    <DialogHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{getCategoryLabel(selectedFeature.category)}</Badge>
                        {selectedFeature.version && (
                          <Badge variant="secondary">v{selectedFeature.version}</Badge>
                        )}
                      </div>
                      <DialogTitle className="text-xl">{selectedFeature.name}</DialogTitle>
                      {selectedFeature.added_date && (
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Ajouté le {new Date(selectedFeature.added_date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', month: 'long', year: 'numeric' 
                          })}
                        </p>
                      )}
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                      {/* Description */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-2">Description</h4>
                        <p className="text-slate-600">{selectedFeature.description}</p>
                      </div>
                      
                      {/* Account Types */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-2">Accessible par</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedFeature.account_types?.map(type => (
                            <Badge 
                              key={type}
                              className="text-white"
                              style={{ backgroundColor: getAccountTypeColor(type) }}
                            >
                              {getAccountTypeLabel(type)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Tutorial Content */}
                      {selectedFeature.tutorial_content && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
                            Guide d'utilisation
                          </h4>
                          <div className="bg-slate-50 rounded-lg p-4 text-slate-600 whitespace-pre-wrap">
                            {selectedFeature.tutorial_content}
                          </div>
                        </div>
                      )}
                      
                      {/* Tutorial Steps */}
                      {selectedFeature.tutorial_steps?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <ChevronRight className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
                            Étapes à suivre
                          </h4>
                          <ol className="list-none space-y-2">
                            {selectedFeature.tutorial_steps.map((step, idx) => (
                              <li key={idx} className="flex items-start gap-3 bg-slate-50 rounded-lg p-3">
                                <span 
                                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                  style={{ backgroundColor: theme?.colors?.accent }}
                                >
                                  {idx + 1}
                                </span>
                                <span className="text-slate-600">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      
                      {/* Related Pages */}
                      {selectedFeature.related_pages?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2">Pages concernées</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedFeature.related_pages.map((page, idx) => (
                              <Badge key={idx} variant="outline" className="cursor-pointer hover:bg-slate-100">
                                {page}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Tutorials Tab */}
          <TabsContent value="tutorials" className="space-y-4">
            {isAdmin && (
              <Button onClick={() => { setEditingItem(null); setShowTutorialDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un tutoriel
              </Button>
            )}
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredTutorials.length === 0 ? (
              <Card className="card">
                <CardContent className="py-12 text-center text-slate-500">
                  Aucun tutoriel trouvé
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredTutorials.map(tutorial => (
                  <Card key={tutorial.id} className="card overflow-hidden">
                    {getYouTubeEmbedUrl(tutorial.youtube_url) && (
                      <div className="aspect-video bg-slate-100">
                        <iframe
                          src={getYouTubeEmbedUrl(tutorial.youtube_url)}
                          className="w-full h-full"
                          allowFullScreen
                          title={tutorial.title}
                        />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2 text-xs">
                            {getCategoryLabel(tutorial.category)}
                          </Badge>
                          <CardTitle className="text-base">{tutorial.title}</CardTitle>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => editTutorial(tutorial)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteTutorial(tutorial.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-3">{tutorial.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {tutorial.account_types?.map(type => (
                            <Badge 
                              key={type} 
                              className="text-xs text-white"
                              style={{ backgroundColor: getAccountTypeColor(type) }}
                            >
                              {getAccountTypeLabel(type)}
                            </Badge>
                          ))}
                        </div>
                        {tutorial.duration_minutes > 0 && (
                          <span className="text-xs text-slate-400">
                            {tutorial.duration_minutes} min
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {ACCOUNT_TYPES.map(accountType => (
                <Card key={accountType.value} className="card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: accountType.color }}
                      />
                      {accountType.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {features.filter(f => f.account_types?.includes(accountType.value)).map(feature => (
                        <div key={feature.id} className="flex items-center gap-2 text-sm text-slate-600">
                          <ChevronRight className="w-3 h-3" />
                          {feature.name}
                        </div>
                      ))}
                      {features.filter(f => f.account_types?.includes(accountType.value)).length === 0 && (
                        <p className="text-sm text-slate-400">Aucune fonctionnalité assignée</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Timeline Tab - Development History */}
          <TabsContent value="timeline" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Historique de développement</h3>
                <p className="text-sm text-slate-500">Suivi chronologique des fonctionnalités ajoutées</p>
              </div>
              {isAdmin && (
                <Button onClick={() => setShowTimelineDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une entrée
                </Button>
              )}
            </div>
            
            {/* Group timeline by month/year */}
            <Card className="card">
              <CardContent className="p-6">
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                  
                  <div className="space-y-6">
                    {timeline.length > 0 ? timeline.map((entry, idx) => {
                      const entryDate = new Date(entry.created_at);
                      const prevEntry = timeline[idx - 1];
                      const prevDate = prevEntry ? new Date(prevEntry.created_at) : null;
                      const showMonthHeader = !prevDate || 
                        entryDate.getMonth() !== prevDate.getMonth() || 
                        entryDate.getFullYear() !== prevDate.getFullYear();
                      
                      return (
                        <div key={entry.id}>
                          {/* Month header */}
                          {showMonthHeader && (
                            <div className="ml-10 mb-4">
                              <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                                {entryDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                              </h4>
                            </div>
                          )}
                          
                          <div className="flex gap-4">
                            {/* Timeline dot */}
                            <div className="relative z-10">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                                style={{ 
                                  backgroundColor: entry.type === 'feature_added' ? '#10B981' :
                                                   entry.type === 'tutorial_added' ? '#6366F1' :
                                                   entry.type === 'bugfix' ? '#EF4444' :
                                                   theme?.colors?.accent 
                                }}
                              >
                                {entry.type === 'feature_added' ? <Sparkles className="w-4 h-4" /> :
                                 entry.type === 'tutorial_added' ? <Video className="w-4 h-4" /> :
                                 entry.type === 'bugfix' ? <Settings className="w-4 h-4" /> :
                                 <Clock className="w-4 h-4" />}
                              </div>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge 
                                      variant="secondary" 
                                      className="text-xs"
                                      style={{ 
                                        backgroundColor: entry.type === 'feature_added' ? '#D1FAE5' :
                                                        entry.type === 'tutorial_added' ? '#E0E7FF' :
                                                        entry.type === 'bugfix' ? '#FEE2E2' :
                                                        '#F1F5F9',
                                        color: entry.type === 'feature_added' ? '#059669' :
                                               entry.type === 'tutorial_added' ? '#4338CA' :
                                               entry.type === 'bugfix' ? '#DC2626' :
                                               '#64748B'
                                      }}
                                    >
                                      {entry.type === 'feature_added' ? '✨ Nouvelle fonctionnalité' :
                                       entry.type === 'tutorial_added' ? '📹 Nouveau tutoriel' :
                                       entry.type === 'bugfix' ? '🐛 Correction' :
                                       '📝 Mise à jour'}
                                    </Badge>
                                  </div>
                                  <h5 className="font-semibold text-slate-800">{entry.title}</h5>
                                  {entry.metadata?.description && (
                                    <p className="text-sm text-slate-600 mt-1">{entry.metadata.description}</p>
                                  )}
                                </div>
                                <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
                                  {entryDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </span>
                              </div>
                              
                              {/* Link to feature if available */}
                              {entry.metadata?.feature_id && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="mt-2 p-0 h-auto text-xs"
                                  onClick={() => {
                                    const feature = features.find(f => f.id === entry.metadata.feature_id);
                                    if (feature) setSelectedFeature(feature);
                                  }}
                                >
                                  Voir la documentation →
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="text-center py-12 text-slate-500">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucune mise à jour enregistrée</p>
                        {isAdmin && (
                          <p className="text-sm mt-1">Commencez à documenter l'historique de développement</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Feature Dialog - Enhanced with Tutorial */}
        <Dialog open={showFeatureDialog} onOpenChange={setShowFeatureDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Modifier la fonctionnalité' : 'Nouvelle fonctionnalité'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nom de la fonctionnalité *</Label>
                  <Input
                    value={featureForm.name}
                    onChange={(e) => setFeatureForm({ ...featureForm, name: e.target.value })}
                    placeholder="Ex: Menu flottant d'actions rapides"
                  />
                </div>
                <div>
                  <Label>Catégorie *</Label>
                  <Select 
                    value={featureForm.category} 
                    onValueChange={(v) => setFeatureForm({ ...featureForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date d'ajout</Label>
                  <Input
                    type="date"
                    value={featureForm.added_date}
                    onChange={(e) => setFeatureForm({ ...featureForm, added_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Description courte *</Label>
                <Textarea
                  value={featureForm.description}
                  onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })}
                  placeholder="Décrivez brièvement la fonctionnalité..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Types de comptes autorisés *</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ACCOUNT_TYPES.map(type => (
                    <Badge
                      key={type.value}
                      variant={featureForm.account_types.includes(type.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      style={featureForm.account_types.includes(type.value) ? { backgroundColor: type.color } : {}}
                      onClick={() => toggleAccountType(type.value, featureForm, setFeatureForm)}
                    >
                      {type.label}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Version (optionnel)</Label>
                <Input
                  value={featureForm.version}
                  onChange={(e) => setFeatureForm({ ...featureForm, version: e.target.value })}
                  placeholder="Ex: 2.0"
                  className="w-32"
                />
              </div>
              
              {/* Tutorial Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
                  Tutoriel écrit (optionnel)
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <Label>Guide d'utilisation</Label>
                    <Textarea
                      value={featureForm.tutorial_content}
                      onChange={(e) => setFeatureForm({ ...featureForm, tutorial_content: e.target.value })}
                      placeholder="Expliquez comment utiliser cette fonctionnalité..."
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Label>Étapes à suivre</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newStep}
                        onChange={(e) => setNewStep(e.target.value)}
                        placeholder="Ajouter une étape..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTutorialStep())}
                      />
                      <Button type="button" variant="outline" onClick={addTutorialStep}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {featureForm.tutorial_steps?.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {featureForm.tutorial_steps.map((step, idx) => (
                          <li key={idx} className="flex items-center gap-2 bg-slate-50 rounded px-3 py-2 text-sm">
                            <span className="font-medium text-slate-500">{idx + 1}.</span>
                            <span className="flex-1">{step}</span>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-red-500"
                              onClick={() => removeTutorialStep(idx)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFeatureDialog(false)}>Annuler</Button>
              <Button 
                onClick={handleSaveFeature} 
                style={{ backgroundColor: theme?.colors?.accent }}
                disabled={!featureForm.name || !featureForm.category || featureForm.account_types.length === 0}
              >
                {editingItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tutorial Dialog */}
        <Dialog open={showTutorialDialog} onOpenChange={setShowTutorialDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Modifier le tutoriel' : 'Nouveau tutoriel'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={tutorialForm.title}
                  onChange={(e) => setTutorialForm({ ...tutorialForm, title: e.target.value })}
                  placeholder="Ex: Comment créer un projet"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={tutorialForm.description}
                  onChange={(e) => setTutorialForm({ ...tutorialForm, description: e.target.value })}
                  placeholder="Décrivez le tutoriel..."
                  rows={2}
                />
              </div>
              <div>
                <Label>URL YouTube</Label>
                <Input
                  value={tutorialForm.youtube_url}
                  onChange={(e) => setTutorialForm({ ...tutorialForm, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select 
                  value={tutorialForm.category} 
                  onValueChange={(v) => setTutorialForm({ ...tutorialForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Types de comptes concernés</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ACCOUNT_TYPES.map(type => (
                    <Badge
                      key={type.value}
                      variant={tutorialForm.account_types.includes(type.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      style={tutorialForm.account_types.includes(type.value) ? { backgroundColor: type.color } : {}}
                      onClick={() => toggleAccountType(type.value, tutorialForm, setTutorialForm)}
                    >
                      {type.label}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={tutorialForm.duration_minutes}
                  onChange={(e) => setTutorialForm({ ...tutorialForm, duration_minutes: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTutorialDialog(false)}>Annuler</Button>
              <Button onClick={handleSaveTutorial} style={{ backgroundColor: theme?.colors?.accent }}>
                {editingItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Timeline Dialog */}
        <Dialog open={showTimelineDialog} onOpenChange={setShowTimelineDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle mise à jour</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={timelineForm.title}
                  onChange={(e) => setTimelineForm({ ...timelineForm, title: e.target.value })}
                  placeholder="Ex: Ajout du menu flottant"
                />
              </div>
              <div>
                <Label>Description (optionnel)</Label>
                <Textarea
                  value={timelineForm.description}
                  onChange={(e) => setTimelineForm({ ...timelineForm, description: e.target.value })}
                  placeholder="Détails de la mise à jour..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select 
                  value={timelineForm.entry_type} 
                  onValueChange={(v) => setTimelineForm({ ...timelineForm, entry_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="update">Mise à jour</SelectItem>
                    <SelectItem value="feature_added">Nouvelle fonctionnalité</SelectItem>
                    <SelectItem value="bug_fix">Correction de bug</SelectItem>
                    <SelectItem value="improvement">Amélioration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTimelineDialog(false)}>Annuler</Button>
              <Button onClick={handleSaveTimeline} style={{ backgroundColor: theme?.colors?.accent }}>
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default TechnicalDocsPage;
